import type { VideoTemplate } from "~/common/videoTemplate/types"
import { useCallback, useEffect, useRef, useState } from "react"
import { arrayMove } from "@dnd-kit/sortable"
import {
  deleteStaffVideoTemplate,
  listStaffTemplateCategories,
  upsertStaffVideoTemplate,
  upsertStaffTemplateCategory,
} from "~/api/staff"
import {
  buildVideoTemplateContent,
  getVideoTemplateError,
  normalizeTemplateCategories,
  readTemplateCategories,
  readVideoTemplate,
} from "~/common/videoTemplate/config"
import { isPositiveTemplateRank } from "~/common/videoTemplate/template"
import {
  getNextCategoryRank,
  loadTemplateCategoryRecords,
} from "~/common/videoTemplate/categories"
import {
  getNextTemplateRank,
  loadStaffTemplateContents,
  saveTemplateOrder,
  type TemplateOrder,
} from "./sorting"

export const TEMPLATE_PAGE_SIZE = 50

export type StaffTemplateRecord = {
  key: string
  position: number
  content: API.VideoTemplate.Content
  config: VideoTemplate | null
  error: string
}

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback

const readRecord = (
  content: API.VideoTemplate.Content,
  index: number,
): StaffTemplateRecord => {
  const key = content.templateId as string
  try {
    const config = readVideoTemplate(content)
    return {
      key,
      position: index + 1,
      content,
      // A legacy invalid rank is managed by the ordering workflow, not the form.
      config: isPositiveTemplateRank(config.rank)
        ? config
        : { ...config, rank: index + 1 },
      error: "",
    }
  } catch (failure) {
    return {
      key,
      position: index + 1,
      content,
      config: null,
      error: errorText(failure, "模板配置格式无效"),
    }
  }
}

export const useVideoTemplates = () => {
  const [records, setRecords] = useState<StaffTemplateRecord[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(TEMPLATE_PAGE_SIZE)
  const [revision, setRevision] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [orderDirty, setOrderDirty] = useState(false)
  const [ordering, setOrdering] = useState(false)
  const [orderError, setOrderError] = useState("")
  const [deleting, setDeleting] = useState("")
  const baseline = useRef<API.VideoTemplate.Content[]>([])
  const retryOrder = useRef(false)
  const normalization = useRef<TemplateOrder | null>(null)
  const saving = useRef(false)
  const refresh = useCallback(() => setRevision((value) => value + 1), [])
  const currentPage = Math.min(
    page,
    Math.max(1, Math.ceil(records.length / pageSize)),
  )
  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError("")
    const load = async () => {
      try {
        const contents = await loadStaffTemplateContents(controller.signal)
        if (controller.signal.aborted) return
        baseline.current = contents
        setRecords(contents.map(readRecord))
        setOrderDirty(false)
        setOrderError("")
        retryOrder.current = false
        normalization.current = null
      } catch (failure) {
        if (!controller.signal.aborted) {
          setRecords([])
          setOrderDirty(false)
          setOrderError("")
          retryOrder.current = false
          setError(errorText(failure, "模板加载失败"))
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }
    void load()
    return () => controller.abort()
  }, [revision])

  const save = async (
    config: VideoTemplate,
    original?: API.VideoTemplate.Content,
  ): Promise<void> => {
    if (saving.current) throw new Error("模板正在处理，请稍候")
    if (orderDirty) throw new Error("请先保存或取消列表顺序")
    const validation = getVideoTemplateError(config)
    if (validation) throw new Error(validation)
    saving.current = true
    try {
      const contents = await loadStaffTemplateContents()
      const index = contents.findIndex(
        (item) => item.templateId === config.templateId.trim(),
      )
      if (!original && index >= 0)
        throw new Error("模板ID已存在，请从列表编辑该模板")
      if (original && index < 0)
        throw new Error("该模板已不存在，请重新加载列表")
      const current = original ? contents[index] : undefined
      let rank = current
        ? (current.rank as number)
        : getNextTemplateRank(contents)
      if (current && (!isPositiveTemplateRank(rank) || normalization.current)) {
        normalization.current ??= {
          ids: contents.map((item) => item.templateId as string),
          baseline: contents,
        }
        await saveTemplateOrder({ ...normalization.current, retry: true })
        rank = normalization.current.ids.indexOf(config.templateId.trim()) + 1
        normalization.current = null
      }
      // 本次新选的分类若尚未登记，依次追加到分类列表末尾
      const previous = current ? readTemplateCategories(current) : []
      const added = normalizeTemplateCategories(config.categories).filter(
        (category) => !previous.includes(category),
      )
      if (added.length) {
        const categories = await loadTemplateCategoryRecords(
          undefined,
          (params, options) =>
            listStaffTemplateCategories(params, options?.signal),
        )
        for (const category of added) {
          if (categories.some((item) => item.category === category)) continue
          // oxlint-disable-next-line no-await-in-loop -- 排序值依赖上一次登记结果
          const { data } = await upsertStaffTemplateCategory({
            category,
            rank: getNextCategoryRank(categories),
          })
          if (data?.category !== category)
            throw new Error("分类登记返回数据无效，请重试")
          categories.push(data)
        }
      }
      await upsertStaffVideoTemplate(
        buildVideoTemplateContent({ ...config, rank }, current),
      )
      setLoading(true)
      if (!original) {
        setPage(Math.ceil((contents.length + 1) / pageSize))
      }
      refresh()
    } finally {
      saving.current = false
    }
  }

  const updateOrder = (items: StaffTemplateRecord[]) => {
    setRecords(
      items.map((record, index) => ({ ...record, position: index + 1 })),
    )
    setOrderDirty(
      retryOrder.current ||
        items.some(
          (item, index) => item.key !== baseline.current[index]?.templateId,
        ),
    )
    setOrderError("")
  }
  const remove = async (key: string): Promise<void> => {
    if (saving.current || loading) throw new Error("模板正在处理，请稍候")
    if (orderDirty || retryOrder.current)
      throw new Error("请先保存或取消列表顺序")
    if (!key.trim() || !records.some((record) => record.key === key))
      throw new Error("该模板已不存在，请重新加载列表")
    saving.current = true
    setDeleting(key)
    try {
      await deleteStaffVideoTemplate({ template_id: key })
      setLoading(true)
      refresh()
    } finally {
      saving.current = false
      setDeleting("")
    }
  }
  const reorder = (items: StaffTemplateRecord[]) => {
    if (loading || saving.current || retryOrder.current) return
    const offset = (currentPage - 1) * pageSize
    updateOrder([
      ...records.slice(0, offset),
      ...items,
      ...records.slice(offset + items.length),
    ])
  }
  const move = (key: string, position: number) => {
    if (
      loading ||
      saving.current ||
      retryOrder.current ||
      !Number.isInteger(position) ||
      position < 1 ||
      position > records.length
    )
      return
    const from = records.findIndex((record) => record.key === key)
    if (from < 0 || from === position - 1) return
    updateOrder(arrayMove(records, from, position - 1))
    setPage(Math.ceil(position / pageSize))
  }

  const saveOrder = async (): Promise<boolean> => {
    if (loading || saving.current || !orderDirty) return false
    saving.current = true
    setOrdering(true)
    setOrderError("")
    try {
      await saveTemplateOrder({
        ids: records.map((record) => record.key),
        baseline: baseline.current,
        retry: retryOrder.current,
      })
      setLoading(true)
      refresh()
      return true
    } catch (failure) {
      retryOrder.current = true
      setOrderError(errorText(failure, "顺序保存失败，请重试"))
      return false
    } finally {
      saving.current = false
      setOrdering(false)
    }
  }

  return {
    records: records.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    ),
    total: records.length,
    page: currentPage,
    pageSize,
    loading,
    error,
    refresh,
    save,
    remove,
    deleting,
    orderDirty,
    ordering,
    orderError,
    reorder,
    move,
    saveOrder,
    setPage: (value: number, size = pageSize) => {
      if (saving.current) return
      setPage(size === pageSize ? value : 1)
      setPageSize(size)
    },
    cancelOrder: () => {
      if (!saving.current) refresh()
    },
  }
}
